import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Layout from "../../../components/layout/layout";
import { SearchHeader } from "../../../components/seachHeader/SearchHeader";
import {
  AlertCircle,
  Check,
  Clock,
  Loader,
  Pencil,
  Star,
  Trash2,
  X,
  Search,
} from "lucide-react";
import ViewModel from "../../../common/ViewModel/ViewModel";
import { useAppSelector, useAppDispatch } from "../../../store/hooks";
import { RootState } from "../../../store";
import {
  fetchAllSessionActivities,
  updateActivityInStore,
  updateActivityNotesInStore,
  cleanupAllListeners,
  fetchUserRatingAndReviews,
} from "../../../store/profileSlice";
import { useSelector } from "react-redux";
import { addFunds } from "../../../services/walletService";
import { createNotification } from "../../../store/slices/notificationSlice";
import { doc, updateDoc, Timestamp, getDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import NoActivity from "../../../assets/image/emtyimg.png";

let expertActivityCounter = 1; // Counter for Expert activities

const generateExpertActivityId = () => {
  const id = `E${String(expertActivityCounter).padStart(3, "0")}`; // Prefix "E" for Expert
  expertActivityCounter += 1; // Increment counter
  return id;
};

const formatStartTime = (timestamp: number) => {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

// Helper function to format duration from hours, minutes, seconds
const formatDuration = (
  hours: number,
  minutes: number,
  seconds: number
): string => {
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

// Calculate duration from start and end timestamps (in seconds)

// Helper function to get timestamp for sorting
const getActivityTimestamp = (activity: any): number => {
  if (activity.startTime) {
    if (activity.startTime._seconds) {
      return activity.startTime._seconds;
    } else if (activity.startTime.seconds) {
      return activity.startTime.seconds;
    } else if (typeof activity.startTime === "number") {
      return activity.startTime;
    }
  }

  if (activity.timestamp) {
    if (activity.timestamp._seconds) {
      return activity.timestamp._seconds;
    } else if (activity.timestamp.seconds) {
      return activity.timestamp.seconds;
    } else if (typeof activity.timestamp === "number") {
      return activity.timestamp;
    }
  }

  return 0; // Default value if no timestamp found
};

// Helper function to handle different timestamp formats
const getFormattedTime = (activity: any) => {
  if (activity.startTime) {
    if (activity.startTime._seconds) {
      return formatStartTime(activity.startTime._seconds);
    } else if (activity.startTime.seconds) {
      return formatStartTime(activity.startTime.seconds);
    } else if (typeof activity.startTime === "number") {
      return formatStartTime(activity.startTime);
    }
  }

  if (activity.timestamp) {
    if (activity.timestamp._seconds) {
      return formatStartTime(activity.timestamp._seconds);
    } else if (activity.timestamp.seconds) {
      return formatStartTime(activity.timestamp.seconds);
    } else if (typeof activity.timestamp === "number") {
      return formatStartTime(activity.timestamp);
    }
  }

  return "N/A";
};

// Reusable empty state component (copied from CustomerActivity)
const NoActivities = () => (
  <div className=" flex justify-center items-center">
    <div className="flex flex-col items-center">
      <img src={NoActivity} alt="No Activity" className="w-[201px] h-[196px]" />
      <p className="text-[#696969] text-[16px] nunito-regular mt-4">
        Oops! Nothing here yet
      </p>
    </div>
  </div>
);

export function ActivityComponents() {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState<boolean>(false);
  const [refundAmount, setRefundAmount] = useState<number>(0);
  const [billedAmount, setBilledAmount] = useState<number>(0);
  const [finalAmount, setFinalAmount] = useState<number>(0);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [isProcessingRefund, setIsProcessingRefund] = useState<boolean>(false);
  const [refundMessage, setRefundMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [adminDescription, setAdminDescription] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredActivities, setFilteredActivities] = useState<any[]>([]);
  const [activeFilters, setActiveFilters] = useState<any>(null);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(
    null
  );
  // Add selectors for user ratings
  const userRatings = useSelector(
    (state: RootState) => state.profile.userRatings
  );
  const ratingsLoading = useSelector(
    (state: RootState) => state.profile.ratingsLoading
  );

  const [selectedRefundActivityId, setSelectedRefundActivityId] = useState<
    string | null
  >(null);
  const itemsPerPage = 10;
  const dispatch = useAppDispatch();

  const [notes, setNotes] = useState([
    {
      id: 1,
      timestamp: "12/06/2024, 07:55:01 PM",
      content:
        "I really appreciate the insights and perspective shared in this article. It's definitely given me something to think about and has helped me see things from a different angle. Thank you for writing and sharing!",
    },
  ]);
  console.log(selectedActivity, "selectedActivity");

  // Reset current page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Update notes when selected activity changes
  useEffect(() => {
    if (selectedActivity && selectedActivity.adminNotes) {
      // Convert notes from activity data format to local state format
      const formattedNotes = selectedActivity.adminNotes.map((note: any) => ({
        id: note.id || Date.now(),
        timestamp: note.timestamp || new Date().toLocaleString(),
        content: note.text || note.content || "",
      }));
      setNotes(formattedNotes);
    }
  }, [selectedActivity]);

  // Fetch org names when selectedActivity changes
  const [expertOrgName, setExpertOrgName] = useState<string>("");
  const [expertTeamName, setExpertTeamName] = useState<string>("");
  const [customerOrgName, setCustomerOrgName] = useState<string>("");
  const [customerTeamName, setCustomerTeamName] = useState<string>("");
  useEffect(() => {
    const fetchOrgNames = async () => {
      setExpertOrgName("");
      setCustomerOrgName("");
      setExpertTeamName("");
      setCustomerTeamName("");
      if (selectedActivity) {
        // Fetch expert org
        const expertOrgId = selectedActivity.expertData?.orgId;
        if (expertOrgId) {
          try {
            const orgRef = doc(db, "organizations", expertOrgId);
            const orgSnap = await getDoc(orgRef);
            if (orgSnap.exists()) {
              setExpertOrgName(orgSnap.data().name || "");
              setExpertTeamName(orgSnap.data().teamName || "");
            }
          } catch (e) {
            setExpertOrgName("");
          }
        }
        // Fetch customer org
        const customerOrgId = selectedActivity.customerData?.orgId;
        if (customerOrgId) {
          try {
            const orgRef = doc(db, "organizations", customerOrgId);
            const orgSnap = await getDoc(orgRef);
            if (orgSnap.exists()) {
              setCustomerOrgName(orgSnap.data().name || "");
              setCustomerTeamName(orgSnap.data().teamName || "");
            }
          } catch (e) {
            setCustomerOrgName("");
          }
        }
      }
    };
    fetchOrgNames();
  }, [selectedActivity]);

  // Pagination handlers
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const sessionActivities = useAppSelector(
    (state: RootState) => state.profile.sessionActivities || []
  );
  const fetchLoading = useAppSelector(
    (state: RootState) => state.profile.fetchLoading
  );
  const fetchError = useAppSelector(
    (state: RootState) => state.profile.fetchError
  );
  const { allActivities } = useSelector((state: RootState) => ({
    allActivities: state.profile.allActivities || [],
  }));
  console.log(allActivities, "allActivities");
  const user = useAppSelector((state: RootState) => state.profile.user);

  console.log(sessionActivities, "sessionActivities");
  useEffect(() => {
    dispatch(fetchAllSessionActivities());

    // Cleanup listeners on unmount
    return () => {
      dispatch(cleanupAllListeners());
    };
  }, [dispatch]);

  const [viewData, setViewData] = useState<any>({});

  const handleSearch = (value: string) => {
    console.log("Searching:", value);
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilter = (filters: any) => {
    console.log("Applying filters:", filters);
    setActiveFilters(filters);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Enhanced filter function that handles both search and filter criteria
  const applyFiltersAndSearch = (activities: any[]) => {
    let filtered = [...activities]; // Apply search filter - Search by Expert Name only
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((activity) => {
        const expertName =
          activity.expertData?.name || activity.metadata?.expertName || "";
        return expertName.toLowerCase().includes(searchLower);
      });
    } // Apply status filter
    if (activeFilters?.status) {
      filtered = filtered.filter((activity) => {
        if (activeFilters.status === "Issue reported") {
          return activity.hasIssue === true;
        } else if (activeFilters.status === "Completed") {
          // An activity is completed if it has no issues and is either explicitly completed or has finished
          return (
            activity.hasIssue !== true &&
            (activity.status === "completed" || activity.endTime)
          );
        }
        return true;
      });
    } // Apply rating filter
    if (activeFilters?.expertRating || activeFilters?.customerRating) {
      filtered = filtered.filter((activity) => {
        const expertRating = activity.expertRating || 0;
        const customerRating = activity.customerRating || 0;

        let meetsCriteria = false;

        // Check expert rating filter
        if (activeFilters.expertRating) {
          const targetExpertRating = parseInt(activeFilters.expertRating);
          if (expertRating == targetExpertRating) {
            meetsCriteria = true;
          }
        }

        // Check customer rating filter
        if (activeFilters.customerRating) {
          const targetCustomerRating = parseInt(activeFilters.customerRating);
          if (customerRating == targetCustomerRating) {
            meetsCriteria = true;
          }
        }

        // If both filters are applied, both must match
        if (activeFilters.expertRating && activeFilters.customerRating) {
          const targetExpertRating = parseInt(activeFilters.expertRating);
          const targetCustomerRating = parseInt(activeFilters.customerRating);
          meetsCriteria =
            expertRating == targetExpertRating &&
            customerRating == targetCustomerRating;
        }

        return meetsCriteria;
      });
    }

    // Apply amount filters
    if (activeFilters?.amountRanges) {
      const { amountRanges } = activeFilters;

      // Check if any amount range is selected
      const hasAmountFilter = Object.keys(amountRanges).some(
        (key) =>
          key !== "customMin" &&
          key !== "customMax" &&
          amountRanges[key] === true
      );

      if (hasAmountFilter || amountRanges.custom) {
        filtered = filtered.filter((activity) => {
          // Get activity amount
          let amount = 0;
          if (activity.cost) {
            amount = parseFloat(activity.cost);
          } else if (activity.metadata?.amount) {
            amount = parseFloat(activity.metadata.amount.replace("$", ""));
          } else if (activity.amount) {
            amount = parseFloat(activity.amount);
          }

          // Check predefined ranges
          if (amountRanges.range1 && amount >= 0 && amount <= 100) return true;
          if (amountRanges.range2 && amount >= 101 && amount <= 200)
            return true;
          if (amountRanges.range3 && amount >= 201 && amount <= 300)
            return true;
          if (amountRanges.range4 && amount >= 301 && amount <= 400)
            return true;
          if (amountRanges.range5 && amount >= 401 && amount <= 500)
            return true;
          if (amountRanges.range6 && amount >= 501) return true;

          // Check custom range
          if (amountRanges.custom) {
            const minAmount = amountRanges.customMin || 0;
            const maxAmount = amountRanges.customMax || Infinity;
            if (amount >= minAmount && amount <= maxAmount) return true;
          }

          return false;
        });
      }
    }

    return filtered;
  }; // Update filtered activities when sessionActivities, searchTerm, or activeFilters change
  useEffect(() => {
    if (!sessionActivities) {
      setFilteredActivities([]);
      return;
    }

    const filtered = applyFiltersAndSearch(sessionActivities);
    setFilteredActivities(filtered);
  }, [sessionActivities, searchTerm, activeFilters]);
  // Handle actions
  const handleView = (data: any) => {
    // Set the selected activity ID to highlight the row
    setSelectedActivityId(data.activityId || data.id);
    if (data.expertId || data.expertData?.id) {
      dispatch(
        fetchUserRatingAndReviews({
          userId: data.expertId || data.expertData.id,
          role: "Expert",
        })
      );
    }

    if (data.customerId || data.customerData?.id) {
      dispatch(
        fetchUserRatingAndReviews({
          userId: data.customerId || data.customerData.id,
          role: "Customer",
        })
      );
    }
    // Make sure duration is properly formatted for the ViewModel
    let calculatedDuration = "";

    // If we have start and end times, calculate the duration
    if (data.endTime && data.startTime) {
      const startSeconds =
        data.startTime._seconds || data.startTime.seconds || data.startTime;
      const endSeconds =
        data.endTime._seconds || data.endTime.seconds || data.endTime;

      if (startSeconds && endSeconds && endSeconds > startSeconds) {
        const durationInSeconds = endSeconds - startSeconds;
        const hours = Math.floor(durationInSeconds / 3600);
        const minutes = Math.floor((durationInSeconds % 3600) / 60);
        const seconds = Math.floor(durationInSeconds % 60);
        calculatedDuration = formatDuration(hours, minutes, seconds);
      }
    }

    // Prepare the formatted data with properly formatted duration
    let formattedDuration = "";
    if (data.duration && typeof data.duration === "object") {
      formattedDuration = formatDuration(
        data.duration.hours || 0,
        data.duration.minutes || 0,
        data.duration.seconds || 0
      );
    } else if (
      typeof data.duration === "string" &&
      (data.duration.includes("h") ||
        data.duration.includes("m") ||
        data.duration.includes("s"))
    ) {
      formattedDuration = data.duration;
    } else if (calculatedDuration) {
      formattedDuration = calculatedDuration;
    } else if (data.metadata?.duration) {
      if (typeof data.metadata.duration === "object") {
        formattedDuration = formatDuration(
          data.metadata.duration.hours || 0,
          data.metadata.duration.minutes || 0,
          data.metadata.duration.seconds || 0
        );
      } else {
        formattedDuration = String(data.metadata.duration);
      }
    } else {
      formattedDuration = "0s";
    }

    const formattedData = {
      ...data,
      duration: formattedDuration,
      originalDuration: data.duration, // Keep the original duration for reference
    };

    console.log("Viewing activity with duration:", formattedData.duration);
    setModalOpen(true);
    setViewData(formattedData);
    setSelectedActivity(formattedData);
  };
  const getInitialsImage = (name: string) => {
    if (!name) return null;
    const initials = name
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase());
    return (
      <div className="w-10 h-10 bg-[#F15D18] rounded-full overflow-hidden">
        <div className="flex items-center justify-center h-full text-white font-bold">
          {initials.join("")}
        </div>
      </div>
    );
  }; // Handle refund modal
  const handleOpenRefundModal = (activity: any) => {
    setSelectedActivity(activity);
    // Set the selected refund activity ID to highlight the row
    setSelectedRefundActivityId(activity.activityId || activity.id);

    // Safely extract amount from different possible locations in the activity object
    let amountStr = "0";
    if (activity.metadata?.amount) {
      amountStr = activity.metadata.amount.replace("$", "");
    } else if (activity.cost) {
      amountStr = activity.cost.toString();
    } else if (activity.amount) {
      amountStr = activity.amount.toString();
    }

    const amount = parseFloat(amountStr) || 0;
    setBilledAmount(amount);
    setRefundAmount(0);
    setFinalAmount(amount);
    setAdminDescription(""); // Reset admin description
    setIsRefundModalOpen(true);
  };
  const handleCloseRefundModal = () => {
    setIsRefundModalOpen(false);
    setSelectedRefundActivityId(null); // Clear selection when refund modal closes
    setRefundAmount(0);
  };

  const handleRefundChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) {
      setRefundAmount(0);
      setFinalAmount(billedAmount);
    } else if (value > billedAmount) {
      setRefundAmount(billedAmount);
      setFinalAmount(0);
    } else {
      setRefundAmount(value);
      setFinalAmount(billedAmount - value);
    }
  };
  const handleProcessRefund = async () => {
    // Allow processing with just a description (no refund amount) to resolve issues
    if (!selectedActivity) return;

    // If there's no refund amount but there's a description, we're just resolving the issue

    setIsProcessingRefund(true);
    setRefundMessage(null);
    try {
      // Get expert and customer IDs from the activity
      const expertId =
        selectedActivity.expertId ||
        selectedActivity.expertData?.id ||
        selectedActivity.metadata?.expertId;

      const customerId =
        selectedActivity.customerId ||
        selectedActivity.customerData?.id ||
        selectedActivity.metadata?.userId ||
        selectedActivity.userId;

      if (!expertId || !customerId) {
        throw new Error("Could not determine expert or customer ID");
      } // Only add refund amount to customer if there's a refund amount
      if (refundAmount > 0) {
        // Add refund amount to customer's wallet (without deducting from expert)
        const addFundsResult = await addFunds(
          customerId, // to customer's wallet
          refundAmount,
          user?.uid || "admin", // admin or current user as creator of this transaction
          "refund" // transaction type
        );

        if (!addFundsResult.success) {
          throw new Error(
            addFundsResult.error || "Failed to add refund to customer wallet"
          );
        }
      }
      // Create notification for the customer about the refund or issue resolution
      try {
        const customerEmail =
          selectedActivity.customerData?.email ||
          selectedActivity.metadata?.email ||
          "customer@example.com";

        // Create notification message with proper handling of admin description
        const formattedDescription = adminDescription.trim()
          ? adminDescription
          : "No additional remarks";

        // Different message based on whether it's a refund or just issue resolution
        let notificationMessage;
        if (refundAmount > 0) {
          notificationMessage = `Update on the reported issue. Refund amount: $${refundAmount.toFixed(
            2
          )}, Admin Remarks: ${formattedDescription}`;
        } else {
          notificationMessage = `Your reported issue has been resolved. Admin Remarks: ${formattedDescription}`;
        }

        // Dispatch the notification creation with formatted message
        dispatch(
          createNotification({
            userId: customerId,
            userEmail: customerEmail,
            message: notificationMessage,
            trigger: refundAmount > 0 ? "issue_resolved-Refund_processed" : "issue_resolved",
            triggeredBy: user?.uid || "admin",
          })
        );

        // Also notify the expert
        try {
          const expertEmail =
            selectedActivity.expertData?.email ||
            selectedActivity.metadata?.expertEmail ||
            "";

          const expertNotificationMessage =
            refundAmount > 0
              ? `Refund processed for activity. Amount: $${refundAmount.toFixed(
                  2
                )}, Admin Remarks: ${formattedDescription}`
              : `Issue resolved for activity. Admin Remarks: ${formattedDescription}`;

          dispatch(
            createNotification({
              userId: expertId,
              userEmail: expertEmail,
              message: expertNotificationMessage,
              trigger: refundAmount > 0 ? "issue_resolved-Refund_processed" : "issue_resolved",
              triggeredBy: user?.uid || "admin",
            })
          );
        } catch (expertNotificationError) {
          console.error("Error creating expert notification:", expertNotificationError);
        }
      } catch (notificationError) {
        console.error("Error creating refund notification:", notificationError);
        // Continue with the process even if notification creation fails
      }

      // Get activity ID
      const activityId = selectedActivity.activityId || selectedActivity.id; // Update in Firestore sessionActivity collection
      try {
        // Create update data - different data depending on whether there's a refund or just resolving issue
        const updateData: any = {
          hasIssue: false, // Reset hasIssue when resolving
          status: "completed",
          adminDescription: adminDescription, // Add admin description
          updatedAt: Timestamp.now(),
        };

        // Add refund-specific fields only if there's a refund amount
        if (refundAmount > 0) {
          // Calculate total refund amount (add new refund to any existing refund)
          const existingRefundAmount = selectedActivity?.refundAmount || 0;
          const totalRefundAmount = existingRefundAmount + refundAmount;

          // Add refund fields to update data
          updateData.hasRefund = true;
          updateData.refundAmount = totalRefundAmount;
          updateData.cost = finalAmount;
          updateData.finalAmount = finalAmount;
          updateData.refundedAt = Timestamp.now();
        }

        // Update in Firestore
        const activityRef = doc(db, "sessionActivity", activityId);
        await updateDoc(activityRef, updateData);

        console.log("Updated activity in Firestore:", activityId);
      } catch (firestoreError) {
        console.error("Error updating activity in Firestore:", firestoreError);
        // Continue with Redux update even if Firestore update fails
      } // Update the activity in Redux store
      dispatch(
        updateActivityInStore({
          activityId: activityId,
          status: "completed",
          adminDescription: adminDescription,
          updatedAt: new Date(),
          ...(refundAmount > 0
            ? {
                hasRefund: true,
                refundAmount: refundAmount,
                cost: finalAmount,
                finalAmount: finalAmount,
                refundedAt: new Date(),
              }
            : {}),
        })
      );

      setRefundMessage({
        type: "success",
        text:
          refundAmount > 0
            ? `Successfully refunded $${refundAmount.toFixed(2)} to customer`
            : "Successfully resolved the issue",
      });

      // Close modal after 2 seconds on success
      setTimeout(() => {
        setIsRefundModalOpen(false);
        setRefundAmount(0);
        setIsProcessingRefund(false);
        setRefundMessage(null);
      }, 2000);
    } catch (error) {
      console.error("Error processing refund:", error);
      setRefundMessage({
        type: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to process refund. Please try again.",
      });
      setIsProcessingRefund(false);
    }
  }; // Format data for Expert tab
  const formatExpertActivities = (activities: any[]) => {
    // Use the activities parameter passed to the function (this allows filtering to work)
    if (activities && activities.length > 0) {
      // Sort activities by timestamp, newest first
      const sortedActivities = [...activities].sort((a, b) => {
        const aTime = getActivityTimestamp(a);
        const bTime = getActivityTimestamp(b);
        return bTime - aTime; // Descending order (newest first)
      });

      return sortedActivities.map((activity) => {
        // Enhanced timestamp handling for different formats
        let formattedStartTime = "N/A";
        let startTimeSeconds = 0;
        let endTimeSeconds = 0;

        // Get startTime in seconds
        if (activity.startTime) {
          if (activity.startTime._seconds) {
            startTimeSeconds = activity.startTime._seconds;
            formattedStartTime = formatStartTime(startTimeSeconds);
          } else if (activity.startTime.seconds) {
            startTimeSeconds = activity.startTime.seconds;
            formattedStartTime = formatStartTime(startTimeSeconds);
          } else if (typeof activity.startTime === "number") {
            startTimeSeconds = activity.startTime;
            formattedStartTime = formatStartTime(startTimeSeconds);
          }
        } else if (activity.timestamp) {
          if (activity.timestamp._seconds) {
            startTimeSeconds = activity.timestamp._seconds;
            formattedStartTime = formatStartTime(startTimeSeconds);
          } else if (activity.timestamp.seconds) {
            startTimeSeconds = activity.timestamp.seconds;
            formattedStartTime = formatStartTime(startTimeSeconds);
          } else if (typeof activity.timestamp === "number") {
            startTimeSeconds = activity.timestamp;
            formattedStartTime = formatStartTime(startTimeSeconds);
          }
        }

        // Get endTime in seconds
        if (activity.endTime) {
          if (activity.endTime._seconds) {
            endTimeSeconds = activity.endTime._seconds;
          } else if (activity.endTime.seconds) {
            endTimeSeconds = activity.endTime.seconds;
          } else if (typeof activity.endTime === "number") {
            endTimeSeconds = activity.endTime;
          }
        } // Calculate duration based on start and end times if both exist
        let calculatedDuration = "";
        if (
          startTimeSeconds > 0 &&
          endTimeSeconds > 0 &&
          endTimeSeconds > startTimeSeconds
        ) {
          const durationInSeconds = endTimeSeconds - startTimeSeconds;
          const hours = Math.floor(durationInSeconds / 3600);
          const minutes = Math.floor((durationInSeconds % 3600) / 60);
          const seconds = Math.floor(durationInSeconds % 60);
          calculatedDuration = formatDuration(hours, minutes, seconds);
        }
        return {
          activityId: activity.activityId || generateExpertActivityId(),
          status: (
            <div
              className={`w-10 h-10 rounded-full ${
                activity.status === "completed"
                  ? "bg-[#2E9D2A14]"
                  : activity.status === "active"
                  ? "bg-[#0000FF14]"
                  : "bg-[#D5332A14] border-[15px] border-red-50"
              } flex items-center justify-center relative group`}
            >
              <div
                className={`w-3 h-3 rounded-full ${
                  activity.status === "Issue reported"
                    ? "bg-[#D5332A]"
                    : activity.status === "active"
                    ? "bg-[#0000FF]"
                    : "bg-[#2E9D2A]"
                }`}
              ></div>
              <div className="absolute z-10 ml-7 opacity-0 group-hover:opacity-100 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-white text-black text-xs rounded-md shadow-lg whitespace-nowrap transition-opacity duration-200 pointer-events-none">
                {activity.status === "completed"
                  ? "Activity Completed"
                  : activity.status === "active"
                  ? "Activity Active"
                  : activity.hasIssue
                  ? "Issue Reported"
                  : "Activity Completed"}
              </div>
            </div>
          ),
          expertName: (
            <div className="flex items-center gap-3 text-[14px] nunito-regular text-[#696969]">
              {activity.expertData?.profileImageUrl ? (
                <img
                  className="h-10 w-10 text-gray-600 rounded-full"
                  src={activity.expertData.profileImageUrl}
                  alt="Profile Image"
                />
              ) : (
                getInitialsImage(activity.expertData?.name || "Expert")
              )}
              <div className="truncate w-40">
                {activity.expertData?.name || "Expert"}
              </div>
            </div>
          ),
          customerName: (
            <div className="flex items-center gap-3 text-[14px] nunito-regular text-[#696969]">
              {activity.customerData?.profileImageUrl ? (
                <img
                  className="h-10 w-10 text-gray-600 rounded-full"
                  src={activity.customerData.profileImageUrl}
                  alt="Profile Image"
                />
              ) : (
                getInitialsImage(activity.customerData?.name || "Customer")
              )}
              <div className="truncate w-40">
                {activity.customerData?.name || "Customer"}
              </div>
            </div>
          ),
          startTime: formattedStartTime,
          duration:
            activity.duration && typeof activity.duration === "object"
              ? formatDuration(
                  activity.duration.hours || 0,
                  activity.duration.minutes || 0,
                  activity.duration.seconds || 0
                )
              : calculatedDuration || "N/A",
          expertRating:
            activity.expertRating || activity.metadata?.expertRating || 0,
          customerRating:
            activity.customerRating || activity.metadata?.customerRating || 0,
          ratings: activity.rating || 0,
          earnings: `$${parseFloat(
            activity.cost || activity.amount || "0"
          ).toFixed(2)}`,
          hasRefund: activity.hasRefund || false,
          action: (
            <div className="flex gap-3 items-center">
              <button
                className="text-[#F15D18] hover:text-[#F15A29] underline"
                onClick={() => {
                  // Calculate duration if it's not already provided
                  const activityWithDuration = {
                    ...activity,
                    duration:
                      activity.duration && typeof activity.duration === "object"
                        ? formatDuration(
                            activity.duration.hours || 0,
                            activity.duration.minutes || 0,
                            activity.duration.seconds || 0
                          )
                        : activity.duration ||
                          activity.metadata?.duration ||
                          calculatedDuration ||
                          "0s",
                  };

                  handleView(activityWithDuration);
                }}
              >
                View
              </button>{" "}
              <button
                className={`${
                  activity.hasIssue ? "cursor-pointer" : " opacity-50"
                } relative group`}
                onClick={() =>
                  activity.hasIssue && handleOpenRefundModal(activity)
                }
                disabled={!activity.hasIssue}
              >
                {" "}
                {activity.hasIssue && (
                  <span className="absolute opacity-0 group-hover:opacity-100 bottom-full right-0 mb-1 px-3 py-1 bg-white text-black text-xs rounded-md shadow-md transition-opacity duration-200 whitespace-nowrap">
                    Issue Refund
                  </span>
                )}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_524_29576)">
                    <path
                      d="M3 12C3 13.78 3.52784 15.5201 4.51677 17.0001C5.50571 18.4802 6.91131 19.6337 8.55585 20.3149C10.2004 20.9961 12.01 21.1743 13.7558 20.8271C15.5016 20.4798 17.1053 19.6226 18.364 18.364C19.6226 17.1053 20.4798 15.5016 20.8271 13.7558C21.1743 12.01 20.9961 10.2004 20.3149 8.55585C19.6337 6.91131 18.4802 5.50571 17.0001 4.51677C15.5201 3.52784 13.78 3 12 3C9.48395 3.00947 7.06897 3.99122 5.26 5.74L3 8"
                      stroke="#919191"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M3 3V8H8"
                      stroke="#919191"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 7V17"
                      stroke="#919191"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M14.5 8.5H10.75C10.2859 8.5 9.84075 8.68437 9.51256 9.01256C9.18437 9.34075 9 9.78587 9 10.25C9 10.7141 9.18437 11.1592 9.51256 11.4874C9.84075 11.8156 10.2859 12 10.75 12H13.25C13.7141 12 14.1592 12.1844 14.4874 12.5126C14.8156 12.8408 15 13.2859 15 13.75C15 14.2141 14.8156 14.6592 14.4874 14.9874C14.1592 15.3156 13.7141 15.5 13.25 15.5H9"
                      stroke="#919191"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_524_29576">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>{" "}
            </div>
          ),
        };
      });
    }

    // Return empty array if no activities provided
    if (!activities || activities.length === 0) {
      return [];
    }

    // Fallback to the original activities format if no session activities
    // Sort activities by timestamp, newest first
    const sortedActivities = [...activities].sort((a, b) => {
      const aTime = getActivityTimestamp(a);
      const bTime = getActivityTimestamp(b);
      return bTime - aTime; // Descending order (newest first)
    });

    return sortedActivities.map((activity) => {
      // Enhanced timestamp handling for different formats
      let formattedStartTime = "N/A";
      if (activity.startTime) {
        if (activity.startTime._seconds) {
          formattedStartTime = formatStartTime(activity.startTime._seconds);
        } else if (activity.startTime.seconds) {
          formattedStartTime = formatStartTime(activity.startTime.seconds);
        } else if (typeof activity.startTime === "number") {
          formattedStartTime = formatStartTime(activity.startTime);
        }
      } else if (activity.timestamp) {
        if (activity.timestamp._seconds) {
          formattedStartTime = formatStartTime(activity.timestamp._seconds);
        } else if (activity.timestamp.seconds) {
          formattedStartTime = formatStartTime(activity.timestamp.seconds);
        } else if (typeof activity.timestamp === "number") {
          formattedStartTime = formatStartTime(activity.timestamp);
        }
      } // Calculate duration from timestamps if available
      let calculatedDuration = "";
      if (activity.startTime && activity.endTime) {
        const startSeconds =
          activity.startTime._seconds ||
          activity.startTime.seconds ||
          activity.startTime;
        const endSeconds =
          activity.endTime._seconds ||
          activity.endTime.seconds ||
          activity.endTime;

        if (startSeconds && endSeconds && endSeconds > startSeconds) {
          const durationInSeconds = endSeconds - startSeconds;
          const hours = Math.floor(durationInSeconds / 3600);
          const minutes = Math.floor((durationInSeconds % 3600) / 60);
          const seconds = Math.floor(durationInSeconds % 60);
          calculatedDuration = formatDuration(hours, minutes, seconds);
        }
      }
      return {
        activityId: generateExpertActivityId(), // Generate unique ID for Expert
        status: (
          <div
            className={`w-10 h-10 rounded-full ${
              activity.hasIssue
                ? "bg-[#D5332A14] border-[15px] border-red-50"
                : "bg-[#2E9D2A14]"
            } flex items-center justify-center relative group`}
          >
            <div
              className={`w-3 h-3 rounded-full ${
                activity.hasIssue ? "bg-[#D5332A]" : "bg-[#2E9D2A]"
              }`}
            ></div>
            <div className="absolute opacity-0 group-hover:opacity-100 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-white text-black text-xs rounded-md shadow-md whitespace-nowrap transition-opacity duration-200">
              {activity.status ||
                (activity.hasIssue ? "Issue Raised" : "Completed")}
            </div>
          </div>
        ),
        expertName: (
          <div className="flex items-center gap-3">
            {activity.metadata?.profileImage ? (
              <img
                className="h-10 w-10 text-gray-600"
                src={activity.metadata.profileImage}
                alt="Profile Image"
              />
            ) : (
              getInitialsImage(activity?.metadata?.expertName)
            )}

            <div>{activity?.metadata?.expertName}</div>
          </div>
        ),
        customerName: (
          <div className="flex items-center gap-3">
            {activity.metadata?.profileImage ? (
              <img
                className="h-10 w-10 text-gray-600"
                src={activity.metadata.profileImage}
                alt="Profile Image"
              />
            ) : (
              getInitialsImage(activity?.metadata?.userName)
            )}

            <div>{activity?.metadata?.userName}</div>
          </div>
        ),
        startTime: formattedStartTime,
        duration: `${
          calculatedDuration ||
          (activity.duration && typeof activity.duration === "object"
            ? formatDuration(
                activity.duration.hours || 0,
                activity.duration.minutes || 0,
                activity.duration.seconds || 0
              )
            : activity.metadata?.duration || "0s")
        }`,
        expertRating:
          activity?.metadata?.expertRating || activity?.expertRating || 0,
        customerRating:
          activity?.metadata?.customerRating || activity?.customerRating || 0,
        ratings: activity?.metadata?.rating || activity?.rating || 0,
        earnings: `$${parseFloat(
          activity?.cost || activity?.metadata?.amount?.replace("$", "") || "0"
        ).toFixed(2)}`,
        hasRefund: activity?.hasRefund || false,
        action: (
          <div className="flex gap-3 items-center">
            <button
              className="text-[#F15D18] hover:text-[#F15A29] underline"
              onClick={() => {
                // Calculate duration if it's not already provided
                const activityWithDuration = {
                  ...activity,
                  duration:
                    calculatedDuration ||
                    (activity.duration && typeof activity.duration === "object"
                      ? formatDuration(
                          activity.duration.hours || 0,
                          activity.duration.minutes || 0,
                          activity.duration.seconds || 0
                        )
                      : activity.metadata?.duration || "0s"),
                };

                handleView(activityWithDuration);
              }}
            >
              View
            </button>
            <button
              className={`${
                activity.hasIssue
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-50"
              }`}
              onClick={() =>
                activity.hasIssue && handleOpenRefundModal(activity)
              }
              disabled={!activity.hasIssue}
              title={
                activity.hasIssue
                  ? "Issue Refund"
                  : "Refunds only available for activities with issues"
              }
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_524_29576)">
                  <path
                    d="M3 12C3 13.78 3.52784 15.5201 4.51677 17.0001C5.50571 18.4802 6.91131 19.6337 8.55585 20.3149C10.2004 20.9961 12.01 21.1743 13.7558 20.8271C15.5016 20.4798 17.1053 19.6226 18.364 18.364C19.6226 17.1053 20.4798 15.5016 20.8271 13.7558C21.1743 12.01 20.9961 10.2004 20.3149 8.55585C19.6337 6.91131 18.4802 5.50571 17.0001 4.51677C15.5201 3.52784 13.78 3 12 3C9.48395 3.00947 7.06897 3.99122 5.26 5.74L3 8"
                    stroke="#919191"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3 3V8H8"
                    stroke="#919191"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12 7V17"
                    stroke="#919191"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M14.5 8.5H10.75C10.2859 8.5 9.84075 8.68437 9.51256 9.01256C9.18437 9.34075 9 9.78587 9 10.25C9 10.7141 9.18437 11.1592 9.51256 11.4874C9.84075 11.8156 10.2859 12 10.75 12H13.25C13.7141 12 14.1592 12.1844 14.4874 12.5126C14.8156 12.8408 15 13.2859 15 13.75C15 14.2141 14.8156 14.6592 14.4874 14.9874C14.1592 15.3156 13.7141 15.5 13.25 15.5H9"
                    stroke="#919191"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_524_29576">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>{" "}
            <button
              className={`${
                activity.hasRefund
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-50"
              }`}
              title={
                activity.hasRefund
                  ? "Process Refund"
                  : "Refunds only available for activities with refund status"
              }
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
                  stroke="#919191"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 12L11 14L15 10"
                  stroke="#919191"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        ),
      };
    });
  };

  const columns = [
    { key: "Status", label: "status", render: (item: any) => item.status },
    {
      key: "expertName",
      label: "Expert Name",
      render: (item: any) => item.expertName,
    },
    {
      key: "customerName",
      label: "Customer Name",
      render: (item: any) => item.customerName,
    },
    { key: "startTime", label: "Start Time" },
    { key: "duration", label: "Duration" },
    {
      key: "expertRating",
      label: "Expert Ratings",
    },
    {
      key: "customerRating",
      label: "Customer Ratings",
    },
    { key: "earnings", label: "Earnings" },
    {
      key: "action",
      label: "Action",
      render: (item: any) => item.action,
    },
  ];

  const handleEditActivity = async (updatedData: any) => {
    console.log("Updated activity data:", updatedData);

    try {
      const activityId =
        updatedData.id ||
        (selectedActivity &&
          (selectedActivity.activityId || selectedActivity.id));

      if (!activityId) {
        throw new Error("Activity ID not found");
      }

      // If notes were updated
      if (updatedData.adminNotes) {
        // Dispatch action to update Redux store
        dispatch(
          updateActivityNotesInStore({
            activityId,
            adminNotes: updatedData.adminNotes,
          })
        );
        // Update local notes state
        const formattedNotes = updatedData.adminNotes.map((note: any) => ({
          id: note.id || Date.now(),
          timestamp: note.timestamp || new Date().toLocaleString(),
          content: note.text || note.content || "",
        }));
        setNotes(formattedNotes);
      }
      // If ratings/reviews were updated
      else {
        // Dispatch action to update Redux store
        dispatch(
          updateActivityInStore({
            activityId,
            expertRating: updatedData.expertRating,
            expertReview: updatedData.expertReview,
            customerRating: updatedData.customerRating,
            customerReview: updatedData.customerReview,
            updatedAt: new Date(),
          })
        );
      }

      // Update the selected activity in the local state
      if (selectedActivity) {
        setSelectedActivity({
          ...selectedActivity,
          ...updatedData,
        });
      }
    } catch (error) {
      console.error("Error updating activity in store:", error);
    }
  };

  console.log(" selectedActivity", selectedActivity);
  return (
    <Layout>
      {" "}
      <div
        className="p-6  mx-auto bg-white  rounded-xl"
        style={{
          height: "calc(100vh - 112px)", // Adjust height to fit within the viewport
          overflowY: "hidden",
            overflowX:'hidden'
        }}
      >
        {" "}
        <SearchHeader
          title="Activity"
          onSearch={handleSearch}
          onFilter={handleFilter}
          adminActivity={true}
        />
        {fetchLoading ? (
          <div className="flex justify-center items-center h-60">
            <Loader className="w-10 h-10 text-[#F15D18] animate-spin" />
          </div>
        ) : fetchError ? (
          <div className="text-red-500 p-4 text-center">{fetchError}</div>
        ) : (
          <>
            <div className="bg-white rounded-[12px] border-[1px] border-[#EBEBEB] overflow-hidden"  style={{
          height: "calc(80vh - 100px)",
          overflowY: "auto",
            overflowX:'hidden'
        }}>
              <table className="min-w-full divide-y max-h-[751px]  divide-gray-200 ">
                <thead className="bg-[#FEF0EA]">
                  <tr>
                    <th className="px-3 py-3 text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Expert Name
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Start Time
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Duration
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Customer Rating
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                    Expert Rating 
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Earnings
                    </th>
                    <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {" "}
                  {(() => {
                    // Format all available filtered activities
                    const formattedFilteredActivities =
                      formatExpertActivities(filteredActivities);

                    // Calculate pagination on filtered and formatted data
                    const indexOfLastItem = currentPage * itemsPerPage;
                    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                    const currentItems = formattedFilteredActivities.slice(
                      indexOfFirstItem,
                      indexOfLastItem
                    );
                    // Show "No results" message if search or filter is active and no results
                    const isSearchOrFilterActive =
                      searchTerm.trim() !== "" ||
                      (activeFilters &&
                        (activeFilters.status !== "" ||
                          activeFilters.expertRating !== "" ||
                          activeFilters.customerRating !== "" ||
                          (activeFilters.amountRanges &&
                            Object.values(
                              activeFilters.amountRanges || {}
                            ).some(Boolean))));

                    if (
                      isSearchOrFilterActive &&
                      formattedFilteredActivities.length === 0
                    ) {
                      return (
                        <tr>
                          <td
                            colSpan={9}
                            className="px-3 py-8 text-center text-gray-500"
                          >
                            <div className="flex flex-col items-center py-20">
                              <div className="w-24 h-24 mb-4 flex items-center justify-center">
                                <Search className="w-16 h-16 text-gray-400" />
                              </div>
                              <h2 className="text-[24px] nunito-semibold text-[#404040] mb-2">
                                Opps! No matching records found.
                              </h2>
                              <p className="text-[#919191] text-[14px] nunito-semibold mb-4 text-center">
                                Try changing the filters or search criteria to
                                see more results!
                              </p>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    // Show "No activities" message if no activities exist at all and no search/filter is active
                    if (
                      !isSearchOrFilterActive &&
                      formattedFilteredActivities.length === 0
                    ) {
                      return (
                        <tr>
                          <td
                            colSpan={9}
                            className="px-3 py-8 text-center text-gray-500"
                          >
                            <NoActivities />
                          </td>
                        </tr>
                      );
                    }
                    return currentItems.map((item, index) => {
                      const isSelected = selectedActivityId === item.activityId;
                      const isRefundSelected =
                        selectedRefundActivityId === item.activityId;
                      const shouldHighlight = isSelected || isRefundSelected;
                      return (
                        <tr
                          key={index}
                          className={
                            shouldHighlight ? "bg-gray-100" : "bg-white"
                          }
                        >
                          <td className="px-3 py-3 whitespace-nowrap">
                            {item.status}
                          </td>
                          <td className="px-3 py-3   whitespace-nowrap">
                            {item.expertName}
                          </td>
                          <td className="px-3 py-3  whitespace-nowrap">
                            {item.customerName}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-[14px] nunito-regular text-[#696969]">
                            {item.startTime}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-[14px] nunito-regular text-[#696969]">
                            {item.duration}
                          </td>{" "}
                          <td className="px-3 py-3 whitespace-nowrap text-[14px] nunito-regular text-[#696969]">
                            <div className="flex items-center">
                              <span>{item.expertRating || 0}</span>
                              <Star className="ml-1 w-[20px] h-[20px] text-[#F5A038] fill-[#F5A038]" />
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-[14px] nunito-regular text-[#696969]">
                            <div className="flex items-center">
                              <span>{item.customerRating || 0}</span>
                              <Star className="ml-1 w-[20px] h-[20px] text-[#F5A038] fill-[#F5A038]" />
                            </div>
                          </td>{" "}
                          <td className="px-3 py-3  whitespace-nowrap text-[14px] nunito-regular text-[#696969]">
                            <div
                              className={`relative${
                                item.hasRefund ? " group" : ""
                              }`}
                            >
                              {item.earnings}{" "}
                              {item.hasRefund === true && (
                                <span className="text-[#F15D18] ">*</span>
                              )}
                              {item.hasRefund && (
                                <div className="absolute z-10 opacity-0 group-hover:opacity-100 top- bottom-5   right-[-100px] px-2 py-1 bg-[#FAFAFA] text-[#696969] text-xs rounded-[8px] shadow-lg transition-opacity duration-200 pointer-events-none w-[216px]">
                                  <div className="text-start">
                                    <div className="nunito-regular text-[12px] text-[#696969] mb-1">
                                      Refund is issued.
                                    </div>
                                    <div className="text-[#696969] nunito-regular text-[12px]">
                                      Click on 'View' to know more details
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {item.action}{" "}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
              
            </div>{" "}
            {/* Pagination controls */}
            {(() => {
              const formattedFilteredActivities =
                formatExpertActivities(filteredActivities);
              const totalPages = Math.ceil(
                formattedFilteredActivities.length / itemsPerPage
              );

              return (
                totalPages > 1 && (
                  <div className="py-3 flex items-center justify-center">
                    <nav className="flex space-x-1">
                      {[...Array(totalPages)].map((_, index) => (
                        <button
                          key={index + 1}
                          onClick={() => handlePageChange(index + 1)}
                          className={`px-2 py-1 text-[#696969] ${
                            currentPage === index + 1
                              ? "bg-[#FAFAFA] border-[#EBEBEB] rounded-[8px] border"
                              : ""
                          }`}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </button>
                      ))}
                    </nav>
                  </div>
                )
              );
            })()}
          </>
        )}
      </div>{" "}
      {isModalOpen && selectedActivity && (
        <ViewModel
          isOpen={isModalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedActivityId(null); // Clear selection when modal closes
          }}
          activityTitle={
            selectedActivity.activityId || selectedActivity.id || ""
          }
          expertName={
            selectedActivity.expertData?.name ||
            selectedActivity.metadata?.expertName ||
            "Expert Name"
          }
          expertEmail={
            selectedActivity.expertData?.email ||
            selectedActivity.metadata?.expertEmail ||
            "expert@example.com"
          }
          expertRating={selectedActivity.expertRating}
          expertRatingUser={
            userRatings[
              selectedActivity.expertId || selectedActivity.expertData?.id
            ]?.avgRating?.toFixed(1) || 0
          }
          expertReviewCount={
            userRatings[selectedActivity.expertId]?.totalReviews || 0
          }
          customerName={
            selectedActivity.customerData?.name ||
            selectedActivity.metadata?.userName ||
            ""
          }
          customerEmail={
            selectedActivity.customerData?.email ||
            selectedActivity.metadata?.email ||
            ""
          }
          customerRating={selectedActivity.customerRating}
          customerRatingUser={
            userRatings[
              selectedActivity.customerId || selectedActivity.customerData?.id
            ]?.avgRating?.toFixed(1) || 0
          }
          customerReviewCount={
            userRatings[
              selectedActivity.customerId || selectedActivity.customerData?.id
            ]?.totalReviews || 0
          }
          activityType={selectedActivity.type || ""}
          activityTime={
            getFormattedTime(selectedActivity) +
            ", " +
            new Date(
              selectedActivity.startTime?.seconds * 1000 ||
                selectedActivity.timestamp?.seconds * 1000 ||
                Date.now()
            ).toLocaleTimeString()
          }
          activityDuration={
            selectedActivity.duration &&
            typeof selectedActivity.duration === "object"
              ? formatDuration(
                  selectedActivity.duration.hours || 0,
                  selectedActivity.duration.minutes || 0,
                  selectedActivity.duration.seconds || 0
                )
              : selectedActivity.duration || "0s"
          }
          activityCost={selectedActivity.cost || "0"}
          activitySavings={selectedActivity.refundAmount || "0"}
          initialNotes={notes.map((note) => ({
            id: note.id,
            text: note.content,
            timestamp: note.timestamp,
          }))}
          onEditActivity={handleEditActivity}
          activityData={selectedActivity} // Pass the entire activity data object
          expertOrgName={expertOrgName}
          customerOrgName={customerOrgName}
          expertTeamName={expertTeamName}
          customerTeamName={customerTeamName}
          expertteamRole={selectedActivity.expertData?.teamRole || ""}
          customerTeamRole={selectedActivity.customerData?.teamRole || ""}
        />
      )}
      {isRefundModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white w-[448px] rounded-[24px] p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[24px] nunito-semibold text-[#0A0A0A]">
                Issue Refund
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={handleCloseRefundModal}
              >
                <X className="w-[32px] h-[32px] text-[#696969]" />
              </button>
            </div>

            <div className="space-y-4 px-4">
              {" "}
              <div className="border border-[#C4C4C4] rounded-[16px] p-5">
                {" "}
                <div>
                  <label className="block text-[16px] nunito-medium text-[#696969] mb-">
                    Issue Details
                  </label>
                  <div className="text-[#F15D18] text-[20px] nunito-bold">
                    {selectedActivity.issueDetails}
                  </div>
                </div>
                <div>
                  <label className="block text-[16px] nunito-medium text-[#696969] mb-1">
                    Billed Amount
                  </label>
                  <div className="text-[#F15D18] text-[20px] nunito-bold">
                    ${billedAmount.toFixed(2)}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-[16px] text-[#404040] nunito-semibold mb-1 mt-2">
                    Refund Amount
                  </label>
                  <input
                    type="number"
                    value={refundAmount === 0 ? "" : refundAmount}
                    onChange={handleRefundChange}
                    max={billedAmount}
                    className="w-full px-3 py-2 border border-[#696969] text-[#404040] text-[14px] rounded-[6px]"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-[16px] nunito-medium text-[#696969] mb-1">
                    Final Amount Charged
                  </label>
                  <div className="text-[#F15D18] text-[20px] nunito-bold">
                    ${finalAmount.toFixed(2)}
                  </div>
                </div>{" "}
                <div className="mt-4">
                  <label className="block text-[16px] nunito-medium text-[#696969] mb-1">
                    Description
                  </label>
                  <textarea
                    value={adminDescription}
                    onChange={(e) => setAdminDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-[#696969] text-[#404040] text-[14px] rounded-[6px]"
                    rows={3}
                    placeholder="Enter refund reason or additional notes"
                  />
                </div>
              </div>{" "}
              <div className="flex justify-center">
                <button
                  className={`w-[120px] h-[40px] bg-[#F15D18] hover:bg-orange-600 text-[#FFFFFF] text-[16px] nunito-semibold py-2 rounded-md flex items-center justify-center ${
                    isProcessingRefund ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                  onClick={handleProcessRefund}
                  disabled={isProcessingRefund}
                >
                  {isProcessingRefund ? (
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                  ) : null}
                  {isProcessingRefund ? "Processing..." : "Save"}
                </button>
              </div>
              {refundMessage && (
                <div
                  className={`mt-4 p-3 rounded-md text-center ${
                    refundMessage.type === "success"
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  }`}
                >
                  <div className="flex items-center justify-center">
                    {refundMessage.type === "success" ? (
                      <Check className="w-5 h-5 mr-2" />
                    ) : (
                      <AlertCircle className="w-5 h-5 mr-2" />
                    )}
                    {refundMessage.text}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Remove or comment out the old modals that are being replaced */}
      {/* {isEditOpen && (
        // ...existing modal code...
      )} */}
      {/* {(isEditReview || isEditReviewExpert) && (
        // ...existing modal code...
      )} */}
    </Layout>
  );
}
